import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, ShieldCheck, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import logoHorizontal from '@/assets/logo-horizontal-bk.svg';
import { getErrorMessage } from '@/lib/errors';

const inputCls = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

export function AceptarInvitacionPage() {
  const [params] = useSearchParams();
  const navigate  = useNavigate();
  const token     = params.get('token') ?? '';

  const [form, setForm]         = useState({ nombre: '', apellidoPaterno: '', password: '', confirm: '' });
  const [showPwd, setShowPwd]   = useState(false);
  const [done, setDone]         = useState(false);

  const { data: inv, isLoading, isError } = useQuery({
    queryKey: ['invitacion', token],
    queryFn: () => api.get(`/api/super-admin/invitacion/${token}`).then(r => r.data),
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (inv?.nombreInvitado) setForm(f => ({ ...f, nombre: inv.nombreInvitado }));
  }, [inv]);

  const aceptarMut = useMutation({
    mutationFn: (d: any) => api.post(`/api/super-admin/invitacion/${token}/aceptar`, d).then(r => r.data),
    onSuccess: () => setDone(true),
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al activar la cuenta')),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error('El nombre es obligatorio');
    if (form.password.length < 8) return toast.error('La contraseña debe tener al menos 8 caracteres');
    if (form.password !== form.confirm) return toast.error('Las contraseñas no coinciden');
    aceptarMut.mutate({ nombre: form.nombre, apellidoPaterno: form.apellidoPaterno, password: form.password });
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center">
          <img src={logoHorizontal} alt="Anik" className="h-10 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Activación de cuenta</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {/* Loading */}
          {isLoading && (
            <div className="flex flex-col items-center gap-3 py-8 text-gray-400">
              <Loader2 className="animate-spin" size={28} />
              <p className="text-sm">Verificando invitación…</p>
            </div>
          )}

          {/* Error — token inválido */}
          {isError && !isLoading && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <AlertCircle className="text-red-400" size={32} />
              <p className="font-semibold text-gray-800">Invitación no válida</p>
              <p className="text-sm text-gray-500">
                Este enlace ha expirado, ya fue utilizado, o es incorrecto.<br />
                Contacta a tu administrador para solicitar una nueva invitación.
              </p>
            </div>
          )}

          {/* Éxito */}
          {done && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <CheckCircle2 className="text-emerald-500" size={40} />
              <p className="font-semibold text-gray-800">¡Cuenta activada!</p>
              <p className="text-sm text-gray-500">Tu cuenta ha sido creada exitosamente. Ya puedes iniciar sesión.</p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm rounded-xl transition-colors"
              >
                Ir al inicio de sesión
              </button>
            </div>
          )}

          {/* Formulario */}
          {inv && !done && !isLoading && (
            <form onSubmit={submit} className="space-y-5">
              {/* Info de clínica */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
                <ShieldCheck className="text-blue-500 mt-0.5 shrink-0" size={18} />
                <div>
                  <p className="text-sm font-semibold text-blue-800">{inv.clinicaNombre}</p>
                  <p className="text-xs text-blue-600 mt-0.5">Ingresarás como administrador de esta clínica</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nombre *</label>
                <input
                  className={inputCls}
                  placeholder="Tu nombre"
                  value={form.nombre}
                  onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Apellido paterno</label>
                <input
                  className={inputCls}
                  placeholder="Apellido paterno"
                  value={form.apellidoPaterno}
                  onChange={e => setForm(f => ({ ...f, apellidoPaterno: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Correo electrónico</label>
                <input className={`${inputCls} bg-gray-50 text-gray-500`} value={inv.email} disabled />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Contraseña *</label>
                <div className="relative">
                  <input
                    className={inputCls}
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    required
                  />
                  <button type="button" onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Confirmar contraseña *</label>
                <input
                  className={inputCls}
                  type="password"
                  placeholder="Repite tu contraseña"
                  value={form.confirm}
                  onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={aceptarMut.isPending}
                className="flex items-center justify-center gap-2 w-full py-3 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white font-semibold text-sm rounded-xl transition-colors"
              >
                {aceptarMut.isPending && <Loader2 size={15} className="animate-spin" />}
                Activar mi cuenta
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
